import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { TagsLabelsService } from '@core/services/tags-labels/tags-labels.service';
import { TagsLabelsActions } from '@core/store/admin/tags-labels/tags-labels.actions';

@Injectable()
export class TagsLabelsEffects {
  private readonly actions$ = inject(Actions);
  private readonly tagsLabelsService = inject(TagsLabelsService);

  loadTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagsLabelsActions.loadTags),
      switchMap(({ dataSpaceId }) =>
        this.tagsLabelsService.listTags$(dataSpaceId).pipe(
          map((tags) => TagsLabelsActions.loadTagsSuccess({ tags })),
          catchError((error) =>
            of(
              TagsLabelsActions.loadTagsFailure({
                error: error?.errorMessage ?? 'Failed to load tags'
              })
            )
          )
        )
      )
    )
  );

  createTag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagsLabelsActions.createTag),
      switchMap(({ data }) =>
        this.tagsLabelsService.createTag$(data).pipe(
          map((tag) => TagsLabelsActions.createTagSuccess({ tag })),
          catchError((error) =>
            of(
              TagsLabelsActions.createTagFailure({
                error: error?.errorMessage ?? 'Failed to create tag'
              })
            )
          )
        )
      )
    )
  );

  updateTag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagsLabelsActions.updateTag),
      switchMap(({ id, data }) =>
        this.tagsLabelsService.updateTag$(id, data).pipe(
          map((tag) => TagsLabelsActions.updateTagSuccess({ tag })),
          catchError((error) =>
            of(
              TagsLabelsActions.updateTagFailure({
                error: error?.errorMessage ?? 'Failed to update tag'
              })
            )
          )
        )
      )
    )
  );

  deleteTag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagsLabelsActions.deleteTag),
      switchMap(({ id }) =>
        this.tagsLabelsService.deleteTag$(id).pipe(
          map(() => TagsLabelsActions.deleteTagSuccess({ id })),
          catchError((error) =>
            of(
              TagsLabelsActions.deleteTagFailure({
                error: error?.errorMessage ?? 'Failed to delete tag'
              })
            )
          )
        )
      )
    )
  );

  loadLabels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagsLabelsActions.loadLabels),
      switchMap(({ entityTypeFilter, dataSpaceId }) =>
        this.tagsLabelsService.listLabels$(entityTypeFilter, dataSpaceId).pipe(
          map((labels) => TagsLabelsActions.loadLabelsSuccess({ labels })),
          catchError((error) =>
            of(
              TagsLabelsActions.loadLabelsFailure({
                error: error?.errorMessage ?? 'Failed to load labels'
              })
            )
          )
        )
      )
    )
  );

  createLabel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagsLabelsActions.createLabel),
      switchMap(({ data }) =>
        this.tagsLabelsService.createLabel$(data).pipe(
          map((label) => TagsLabelsActions.createLabelSuccess({ label })),
          catchError((error) =>
            of(
              TagsLabelsActions.createLabelFailure({
                error: error?.errorMessage ?? 'Failed to create label'
              })
            )
          )
        )
      )
    )
  );

  updateLabel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagsLabelsActions.updateLabel),
      switchMap(({ id, data }) =>
        this.tagsLabelsService.updateLabel$(id, data).pipe(
          map((label) => TagsLabelsActions.updateLabelSuccess({ label })),
          catchError((error) =>
            of(
              TagsLabelsActions.updateLabelFailure({
                error: error?.errorMessage ?? 'Failed to update label'
              })
            )
          )
        )
      )
    )
  );

  deleteLabel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagsLabelsActions.deleteLabel),
      switchMap(({ id }) =>
        this.tagsLabelsService.deleteLabel$(id).pipe(
          map(() => TagsLabelsActions.deleteLabelSuccess({ id })),
          catchError((error) =>
            of(
              TagsLabelsActions.deleteLabelFailure({
                error: error?.errorMessage ?? 'Failed to delete label'
              })
            )
          )
        )
      )
    )
  );
}
